"use client";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getBatchQuizHistories,
  deleteBatchQuizHistory,
} from "../../../services/BatchQuizHistoryService";
import { getcourses } from "../../../services/CourseModule";
import { getTrainers, getTrainerByUserId } from "../../../services/TrainerService";
import { getMe } from "../../../services/AuthService";
import { useAuth } from "../../../context/AuthContext";
import { toast } from '../common/Toast/toast';
import NewCustomAsyncSelectDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import NewCustomDataTable from "../../components/common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../../components/common/useScrollLoadMore";
import AssignQuizToBatch from "./AssignQuizToBatch.jsx";
import debounce from "lodash/debounce";
import moment from "moment";
import "./TrainerDashboard.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import { confirm } from '../common/Confirm/confirm';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone';

const ListQuizToBatch = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  // A save in the form popup, or a delete from the view popup, is confirmed
  // in that popup, which closes itself a few seconds later.
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const { done: detailsDone } = usePopupDone(Boolean(details));

  // Read before the fetch awaits anything: a reset mid-flight would turn
  // an append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const listEndRef = useRef(null);
  
  // UI search state
  const [uiSearchTerm, setUiSearchTerm] = useState('');
  const [uiSelectedField, setUiSelectedField] = useState('all');
  
  // Applied search state
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState('');
  
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [actualTrainerId, setActualTrainerId] = useState('');
  const [trainerDataLoaded, setTrainerDataLoaded] = useState(false);
  
  // UI filter states
  const [uiCourseId, setUiCourseId] = useState('');
  const [uiTrainerId, setUiTrainerId] = useState('');
  
  // Applied filter states
  const [appliedCourseId, setAppliedCourseId] = useState('');
  const [appliedTrainerId, setAppliedTrainerId] = useState('');
  
  // Dropdown options with pagination
  const [courseOptions, setCourseOptions] = useState([]);
  const [trainerOptions, setTrainerOptions] = useState([]);
  
  // Pagination state for dropdowns
  const [coursePage, setCoursePage] = useState(1);
  const [trainerPage, setTrainerPage] = useState(1);
  const [courseHasMore, setCourseHasMore] = useState(true);
  const [trainerHasMore, setTrainerHasMore] = useState(true);
  const [courseLoading, setCourseLoading] = useState(false);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [trainerSearch, setTrainerSearch] = useState('');
  
  // Selected dropdown values
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  const isFetching = useRef(false);
  const hasInitialized = useRef(false);
  const didFetchProfile = useRef(false);

  useEffect(() => {
    if (!hasPermission("list-quiz-to-batch", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to assign quizzes to batch.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const fetchUserProfile = useCallback(async () => {
    if (didFetchProfile.current) return;
    didFetchProfile.current = true;

    try {
      const response = await getMe();
      const userData = response.data || response;
      if (userData.isSuperAdmin === true) {
        setIsAdmin(true);
        setTrainerDataLoaded(true);
      } else {
        const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
        const isTrainerUser = profileLabel === 'trainer';
        setIsTrainer(isTrainerUser);
        setIsAdmin(profileLabel === 'admin' || userData.isSuperAdmin);
        
        if (isTrainerUser) {
          const userId = userData.id || userData._id || userData.userId;
          
          try {
            const trainerResponse = await getTrainerByUserId(userId);
            if (trainerResponse && trainerResponse.data) {
              const trainerObjectId = trainerResponse.data.id || trainerResponse.data._id;
              setActualTrainerId(trainerObjectId);
              console.log("Actual Trainer ID set to:", trainerObjectId);
              setTrainerDataLoaded(true);
            } else {
              setActualTrainerId(userId);
              setTrainerDataLoaded(true);
            }
          } catch (error) {
            console.error("Error fetching trainer data:", error);
            setActualTrainerId(userId);
            setTrainerDataLoaded(true);
          }
        } else {
          setTrainerDataLoaded(true);
        }
      }
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setTrainerDataLoaded(true);
    }
  }, []);

  // Fetch courses with pagination and search
  const fetchCourses = useCallback(async (page = 1, search = '', append = false) => {
    if (courseLoading) return;
    setCourseLoading(true);
    
    try {
      const limit = 20;
      const response = await getcourses(limit, page, search);
      const coursesData = response.data || [];
      const pagination = response.pagination || {};
      
      const options = coursesData.map((course) => ({
        value: course.id,
        label: course.name,
      }));
      
      if (append) {
        setCourseOptions(prev => [...prev, ...options]);
      } else {
        setCourseOptions(options);
      }
      
      setCourseHasMore(pagination.hasNext || pagination.hasNextPage || false);
      setCoursePage(page);
      return options;
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Error loading courses');
      return [];
    } finally {
      setCourseLoading(false);
    }
  }, [courseLoading]);

  // Fetch trainers with pagination and search
  const fetchTrainers = useCallback(async (page = 1, search = '', append = false) => {
    if (trainerLoading) return;
    setTrainerLoading(true);
    
    try {
      const limit = 20;
      const response = await getTrainers(limit, page, search);
      const trainersData = response.data || [];
      const pagination = response.pagination || {};
      
      const options = trainersData.map((trainer) => ({
        value: trainer.id,
        label: `${trainer.trainerName} | ${trainer.trainerEmail}`,
        email: trainer.trainerEmail,
      }));
      
      if (append) {
        setTrainerOptions(prev => [...prev, ...options]);
      } else {
        setTrainerOptions(options);
      }
      
      setTrainerHasMore(pagination.hasNext || pagination.hasNextPage || false);
      setTrainerPage(page);
      return options;
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Error loading trainers');
      return [];
    } finally {
      setTrainerLoading(false);
    }
  }, [trainerLoading]);

  // Fetch quiz assignments with server-side pagination and filters
  const fetchAssignments = useCallback(async () => {
    if (isFetching.current) return;

    isFetching.current = true;

    // Read before anything awaits: a ref reset mid-flight would turn an
    // append into a replace and snap the list back to its first page.
    const isAppend = appendRef.current;

    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);
      
      // Determine which trainer ID to use for filtering - similar to ListQuiz
      let trainerIdFilter = '';
      if (isTrainer && actualTrainerId) {
        trainerIdFilter = actualTrainerId;
      } else if (isAdmin && appliedTrainerId) {
        trainerIdFilter = appliedTrainerId;
      }
      
      console.log('Fetching quiz assignments with params:', {
        limit: recordsPerPage,
        page: currentPage,
        search: appliedSearchTerm,
        searchField: appliedSearchField,
        trainerId: trainerIdFilter,
        courseId: appliedCourseId
      });
      
      const response = await getBatchQuizHistories(
        recordsPerPage,
        currentPage,
        appliedSearchTerm,
        appliedSearchField,
        appliedCourseId,
        trainerIdFilter
      );
      
      const assignmentsData = response.data || [];
      const pagination = response.pagination || {};
      
      // Process the data - extract from the nested structure
      const processedData = assignmentsData.map((assignment) => {
        // Extract trainer info
        const trainer = Array.isArray(assignment.trainerId) && assignment.trainerId.length > 0 
          ? assignment.trainerId[0] 
          : null;
        
        // Extract course info
        const course = Array.isArray(assignment.courseId) && assignment.courseId.length > 0 
          ? assignment.courseId[0] 
          : null;
        
        // Extract quiz info
        const quiz = Array.isArray(assignment.quizId) && assignment.quizId.length > 0 
          ? assignment.quizId[0] 
          : null;
        
        // Extract batch info
        const batches = Array.isArray(assignment.batchIds) ? assignment.batchIds : [];
        
        const trainerName = trainer ? trainer.trainerName : 'Unknown Trainer';
        const trainerEmail = trainer ? trainer.trainerEmail : '';
        
        return {
          ...assignment,
          trainerDisplayName: trainerEmail ? `${trainerName} (${trainerEmail})` : trainerName,
          trainerUser: trainer,
          courseDisplayName: course ? course.name : 'Unknown Course',
          course: course,
          quizDisplayName: quiz ? quiz.title : 'Unknown Quiz',
          quiz: quiz,
          batchNames: batches.map(batch => batch.name || 'Unknown Batch'),
          batchObjects: batches,
        };
      });
      
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...processedData.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(processedData);
      }
      appendRef.current = false;
      setTotalRecords(pagination.total || processedData.length);
      console.log('Fetched data:', processedData.length, 'Total records:', pagination.total);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      toast.error("Failed to fetch quiz assignments. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
  }, [recordsPerPage, currentPage, appliedSearchTerm, appliedSearchField, appliedCourseId, appliedTrainerId, isTrainer, actualTrainerId, isAdmin]);

  // Fetch assignments whenever dependencies change
  useEffect(() => {
    if (trainerDataLoaded) {
      fetchAssignments();
    }
  }, [fetchAssignments, trainerDataLoaded]);

  // Initialize user profile
  useEffect(() => {
    const init = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;
      await fetchUserProfile();
    };
    init();
  }, [fetchUserProfile]);

  // Handle search with debounce
  const handleSearchChange = useMemo(
    () => debounce((value, field) => {
      appendRef.current = false;
      setAppliedSearchTerm(value);
      setAppliedSearchField(field !== 'all' ? field : '');
      setCurrentPage(1);
    }, 500),
    []
  );

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setUiSelectedField(field);
    handleSearchChange(value, field);
  };

  // Apply filters
  const handleApplyFilters = () => {
    appendRef.current = false;
    setAppliedCourseId(uiCourseId);
    setAppliedTrainerId(uiTrainerId);
    setCurrentPage(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    appendRef.current = false;
    setUiCourseId('');
    setUiTrainerId('');
    setSelectedCourse(null);
    setSelectedTrainer(null);
    setAppliedCourseId('');
    setAppliedTrainerId('');
    setCurrentPage(1);
  };

  // Handle course filter change (UI only, not applied until Apply button clicked)
  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
    setUiCourseId(selectedOption ? selectedOption.value : '');
  };

  // Handle trainer filter change (UI only, not applied until Apply button clicked)
  const handleTrainerChange = (selectedOption) => {
    setSelectedTrainer(selectedOption);
    setUiTrainerId(selectedOption ? selectedOption.value : '');
  };

  const handleDelete = async (id) => {
    const res = await confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this quiz assignment?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      tone: 'danger',
    });
    
    if (!res.isConfirmed) return;

    try {
      await deleteBatchQuizHistory(id);
      // Gone: the view popup says so in place of the record, then closes.
      const newTotalRecords = totalRecords - 1;
      const newTotalPages = Math.ceil(newTotalRecords / recordsPerPage);
      appendRef.current = false;
      if (currentPage > newTotalPages && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchAssignments();
      }
      toast.success("Assignment deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete the assignment. Please try again.");
    }
  };

  const hasMoreAssignments = data.length < totalRecords;

  // The scroll asked for the next batch. Only ever moves forward, and
  // only while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (data.length >= totalRecords) return;
    appendRef.current = true;
    // Derived from the rows already on screen rather than counted up, so
    // the batch size can change mid-list without asking for a page that
    // has already been read. 30 loaded at a size of 15 is page 3, not
    // page 2. Any overlap a ragged division causes is dropped by the id
    // guard where the rows are appended.
    const nextPage = Math.floor(data.length / recordsPerPage) + 1;
    setCurrentPage(nextPage);
  }, [loading, loadingMore, data.length, totalRecords, recordsPerPage]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore. Its 320px
  // margin starts the fetch before the reader reaches the end, instead of
  // the 150px that let them arrive on an empty foot and wait.
  useScrollLoadMore(listEndRef, loadMore, [loadMore]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  // A window that has not opened yet, is open now, or has closed. Said as a
  // pill rather than a word, so a scan down the column reads as three shapes
  // instead of three sentences.
  const statusOf = (startDate, endDate) => {
    if (!startDate || !endDate) return { label: 'Unknown', tone: '' };
    const now = Date.now();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return { label: 'Unknown', tone: '' };
    if (now < start) return { label: 'Scheduled', tone: 'pg-pill--warn' };
    if (now <= end) return { label: 'Live', tone: 'pg-pill--live' };
    return { label: 'Closed', tone: 'pg-pill--good' };
  };

  const StatusPill = ({ startDate, endDate }) => {
    const { label, tone } = statusOf(startDate, endDate);
    return <span className={`pg-pill ${tone}`.trim()}>{label}</span>;
  };

  const formatDate = (dateString) =>
    dateString ? moment(dateString).format('DD MMM YYYY, HH:mm') : '—';

  const formatDay = (dateString) =>
    dateString ? moment(dateString).format('DD MMM YYYY') : '—';

  const headers = [
    {
      title: 'Quiz',
      prop: 'quizTitle',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <div className="qb-cell">
          <span className="qb-quiz">{row.quizDisplayName}</span>
          <span className="qb-sub">{row.courseDisplayName}</span>
        </div>
      ),
    },
    {
      title: 'Batches',
      prop: 'batchNames',
      cell: (row) => {
        const names = row.batchNames || [];
        if (names.length === 0) return <span className="qb-muted">No batches</span>;
        // The first two are named; the rest are a count, so one assignment
        // covering thirty batches does not push every other column off.
        const shown = names.slice(0, 2).join(', ');
        return (
          <span className="qb-batches">
            {shown}
            {names.length > 2 && (
              <span className="qb-more">+{names.length - 2} more</span>
            )}
          </span>
        );
      },
    },
    ...(isTrainer
      ? []
      : [
          {
            title: 'Trainer',
            prop: 'trainerName',
            sortable: true,
            filterable: true,
            cell: (row) => (
              <span className="qb-trainer">
                {row.trainerUser?.trainerName || row.trainerDisplayName}
              </span>
            ),
          },
        ]),
    {
      title: 'Open',
      prop: 'startDate',
      sortable: true,
      cell: (row) => (
        <div className="qb-cell">
          <span className="qb-when">{formatDay(row.startDate)}</span>
          <span className="qb-sub">to {formatDay(row.endDate)}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      prop: 'status',
      cell: (row) => <StatusPill startDate={row.startDate} endDate={row.endDate} />,
    },
  ];

  const indexedData = data.map((item, index) => ({
    ...item,
    srno: index + 1,
  }));

  // Handle refresh button for dropdowns
  const handleCourseRefresh = async () => {
    setCourseSearch('');
    setCoursePage(1);
    setCourseHasMore(true);
    await fetchCourses(1, '', false);
  };

  const handleTrainerRefresh = async () => {
    setTrainerSearch('');
    setTrainerPage(1);
    setTrainerHasMore(true);
    await fetchTrainers(1, '', false);
  };

  const canEdit = hasPermission('list-quiz-to-batch', 'update', 'lms');
  const canDelete = hasPermission('list-quiz-to-batch', 'delete', 'lms');

  return (
    <div className="pg">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission('list-quiz-to-batch', 'add', 'lms') && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ recordId: null })}
            >
              Assign quiz
            </button>
          )}
        </div>
      </header>

      {/* Everything that narrows the list, search included -- the table's
          own toolbar is switched off below so the two are not stacked one
          above the other. */}
      <FilterSheet label="Filter assignments">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="qb-search">
              Search
            </label>
            <input
              id="qb-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by quiz, course or trainer..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, uiSelectedField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="qb-search-field">
              Search in
            </label>
            <select
              id="qb-search-field"
              className="pg-select"
              value={uiSelectedField}
              onChange={(e) => {
                setUiSelectedField(e.target.value);
                onSearchChange(uiSearchTerm, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="quizDisplayName">Quiz title</option>
              <option value="courseName">Course</option>
              <option value="trainerName">Trainer</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="qb-rows">
              Rows per load
            </label>
            <select
              id="qb-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {isAdmin && (
          <div className="pg-filters-row">
            <div className="pg-filters-fields pg-filters-fields--notrainer">
              <div className="pg-field">
                <label className="pg-label">Course</label>
                <NewCustomAsyncSelectDropdown
                  options={courseOptions}
                  value={selectedCourse ? [selectedCourse] : []}
                  onChange={(selectedOptions) =>
                    handleCourseChange(
                      selectedOptions && selectedOptions.length > 0
                        ? selectedOptions[0]
                        : null,
                    )
                  }
                  placeholder="All courses"
                  isMulti={false}
                  isLoading={courseLoading}
                  searchInControl={true}
                  onMenuOpen={async () => {
                    setCoursePage(1);
                    setCourseSearch('');
                    await fetchCourses(1, '', false);
                  }}
                  onInputChange={debounce(async (searchValue) => {
                    setCourseSearch(searchValue);
                    setCoursePage(1);
                    await fetchCourses(1, searchValue, false);
                  }, 500)}
                  onMenuScrollToBottom={async () => {
                    if (courseHasMore && !courseLoading) {
                      await fetchCourses(coursePage + 1, courseSearch, true);
                    }
                  }}
                  onRefresh={handleCourseRefresh}
                />
              </div>

              <div className="pg-field">
                <label className="pg-label">Trainer</label>
                <NewCustomAsyncSelectDropdown
                  options={trainerOptions}
                  value={selectedTrainer ? [selectedTrainer] : []}
                  onChange={(selectedOptions) =>
                    handleTrainerChange(
                      selectedOptions && selectedOptions.length > 0
                        ? selectedOptions[0]
                        : null,
                    )
                  }
                  placeholder="All trainers"
                  isMulti={false}
                  isLoading={trainerLoading}
                  searchInControl={true}
                  onMenuOpen={async () => {
                    setTrainerPage(1);
                    setTrainerSearch('');
                    await fetchTrainers(1, '', false);
                  }}
                  onInputChange={debounce(async (searchValue) => {
                    setTrainerSearch(searchValue);
                    setTrainerPage(1);
                    await fetchTrainers(1, searchValue, false);
                  }, 500)}
                  onMenuScrollToBottom={async () => {
                    if (trainerHasMore && !trainerLoading) {
                      await fetchTrainers(trainerPage + 1, trainerSearch, true);
                    }
                  }}
                  onRefresh={handleTrainerRefresh}
                />
              </div>
            </div>

            <div className="pg-filters-actions">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
              <button
                type="button"
                className="pg-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </FilterSheet>

      <div className="td-list td-list--quiz">
        <NewCustomDataTable
          data={indexedData}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      {/* Where the reader is, and the sentinel the observer watches. */}
      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more assignments...</span>
          </>
        ) : hasMoreAssignments ? (
          <span>
            {data.length} of {totalRecords} assignments
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} assignment{totalRecords === 1 ? '' : 's'} loaded
            </span>
          )
        )}
      </div>

      {/* Assigning is one step -- six answers, asked once -- so the
          dialog holds the whole form rather than a strip of tabs. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qb-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Assignment</span>
                <h3 className="pg-modal-title" id="qb-form-title">
                  {formModal.recordId ? 'Edit assignment' : 'Assign a quiz'}
                </h3>
                <p className="pg-modal-sub">
                  Pick the quiz and the batches, and say when it is open.
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <AssignQuizToBatch
                embedded
                recordId={formModal.recordId}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the popup shows the message in place of the form and
                  // closes itself a few seconds later.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setDetails(null);
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchAssignments();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {/* One assignment, in full. The actions live here rather than in a
          column of icons, so a row is a thing you open, not a thing you
          aim at. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qb-details-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Assignment</span>
                <h3 className="pg-modal-title" id="qb-details-title">
                  {details.quizDisplayName}
                </h3>
                <p className="pg-modal-sub">{details.courseDisplayName}</p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {detailsDone ? (
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              ) : (
              <>
              <dl className="quiz-facts">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <StatusPill
                      startDate={details.startDate}
                      endDate={details.endDate}
                    />
                  </dd>
                </div>
                <div>
                  <dt>Opens</dt>
                  <dd>{formatDate(details.startDate)}</dd>
                </div>
                <div>
                  <dt>Closes</dt>
                  <dd>{formatDate(details.endDate)}</dd>
                </div>
                <div>
                  <dt>Trainer</dt>
                  <dd>{details.trainerUser?.trainerName || '—'}</dd>
                </div>
                <div>
                  <dt>Trainer email</dt>
                  <dd>{details.trainerUser?.trainerEmail || '—'}</dd>
                </div>
                <div>
                  <dt>Course</dt>
                  <dd>{details.courseDisplayName}</dd>
                </div>
              </dl>

              <h5 className="pgm-section-title">
                Batches
                <span className="pgm-section-count">
                  {details.batchNames?.length || 0}
                </span>
              </h5>
              {details.batchNames && details.batchNames.length > 0 ? (
                <ul className="qb-batchlist">
                  {details.batchNames.map((name, i) => (
                    <li key={`${name}-${i}`}>{name}</li>
                  ))}
                </ul>
              ) : (
                <p className="qb-muted">No batches on this assignment.</p>
              )}
              </>
              )}
            </div>

            {!detailsDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = details.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit assignment
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = details.id;
                      handleDelete(id);
                    }}
                  >
                    Delete assignment
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListQuizToBatch;
